/**
 * The reports' dates, as `YYYY-MM-DD` strings on the company's calendar:
 * the default range and as-of date (inside an auditor's months when the
 * person is limited to some), the comparison date, and the months a fiscal
 * year offers. Pure, so the window clamp is testable.
 */
import type { components } from "@linkhr/api-client/core";

type LedgerWindow = components["schemas"]["CapabilityLedgerWindow"];
type FiscalYear = components["schemas"]["AcctFiscalYear"];

const day = (date: Date): string => date.toISOString().slice(0, 10);
const utc = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

/** `YYYY-MM` → its first day. */
export const firstOfMonth = (month: string): string => `${month.slice(0, 7)}-01`;

/** `YYYY-MM` (or any day in it) → its last day. */
export function endOfMonth(month: string): string {
  const next = utc(firstOfMonth(month));
  next.setUTCMonth(next.getUTCMonth() + 1);
  next.setUTCDate(0);
  return day(next);
}

export function addDays(iso: string, days: number): string {
  const date = utc(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return day(date);
}

/** The same day a year earlier; 29 February becomes the 28th. */
export function yearBefore(iso: string): string {
  const [year = "0", month = "01", date = "01"] = iso.split("-");
  const previous = `${Number(year) - 1}-${month}`;
  return date > endOfMonth(previous).slice(8) ? endOfMonth(previous) : `${previous}-${date}`;
}

/**
 * The default as-of date: today, or the last day of an auditor's last month
 * when that comes first.
 */
export function defaultAsOf(today: string, window: LedgerWindow | null): string {
  if (window === null) return today;
  const last = endOfMonth(window.to);
  return last < today ? last : today;
}

/**
 * The default range: the month of the default as-of date, from its first day
 * (never before an auditor's first month) to that date.
 */
export function defaultRange(
  today: string,
  window: LedgerWindow | null,
): { from: string; to: string } {
  const to = defaultAsOf(today, window);
  const start = firstOfMonth(to);
  const floor = window !== null ? firstOfMonth(window.from) : start;
  return { from: start < floor ? floor : start, to };
}

export const COMPARISONS = ["none", "previous", "last_year"] as const;
export type Comparison = (typeof COMPARISONS)[number];

/** The day a trial balance compares with: the day before it starts, or a year before it ends. */
export function compareDate(comparison: Comparison, from: string, to: string): string | undefined {
  if (comparison === "previous") return addDays(from, -1);
  if (comparison === "last_year") return yearBefore(to);
  return undefined;
}

/** Every month of the company's fiscal years, oldest first, as `YYYY-MM`. */
export function fiscalMonths(years: readonly FiscalYear[]): string[] {
  return years.flatMap((year) => {
    const months: string[] = [];
    for (let month = year.start.slice(0, 7); month <= year.end.slice(0, 7); ) {
      months.push(month);
      month = addDays(endOfMonth(month), 1).slice(0, 7);
    }
    return months;
  });
}
